import { useState } from 'react';
import { useForm } from '@mantine/form';

import { useShowResponseStatus } from '@/shared';
import { useUpdateConversionPairMutation } from '../store/conversion-pairs-api';
import { handleConversionPairError } from '../utils/helpers/conversion-pairs-errors';

import type { TConversionPair, TConversionPairUpdateParams } from '../store/types';

type FormData = TConversionPairUpdateParams;

type Params = {
  conversionPair: TConversionPair;
  onClose: () => void;
};

export const useUpdateConversionPair = ({ conversionPair, onClose }: Params) => {
  const [autoRefresh, setAutoRefresh] = useState(conversionPair.autoRefresh);
  const [updateConversionPair, { isLoading, isSuccess, error }] = useUpdateConversionPairMutation();
  const updateError = handleConversionPairError(error);

  const form = useForm<FormData>({
    mode: 'uncontrolled',
    initialValues: {
      pairId: conversionPair.id,
      rate: Number(conversionPair.rate),
      markupPercent: Number(conversionPair.markupPercent),
      autoRefresh: conversionPair.autoRefresh,
    },
    validate: {
      rate: (value, values) => !values.autoRefresh && (!value || value <= 0) ? 'Введите курс' : null,
      markupPercent: (value) => value <= -100 ? 'Наценка должна быть больше -100%' : null,
    },
  });

  form.watch('autoRefresh', ({ value }) => {
    setAutoRefresh(value);
  });

  useShowResponseStatus({
    isSuccess,
    successMessage: 'Торговая пара успешно обновлена',
    error: updateError,
  });

  const handleSubmit = async (values: FormData) => {
    const params: TConversionPairUpdateParams = {
      ...values,
      rate: !values.autoRefresh && form.isDirty('rate') ? values.rate : undefined,
    };

    const result = await updateConversionPair(params);
    if ('error' in result) {
      return;
    }

    onClose();
  };

  return {
    form,
    isLoading,
    autoRefresh,
    handleSubmit,
  };
};
